import React, { Fragment, ReactElement } from "react";
import DOMPurify from "dompurify";

type HtmlParsedAnswer = {
    answerFragments: ReactElement[];
    citations: string[];
};

export function parseAnswerToHtml(answer: string, isStreaming: boolean, onCitationClicked: (filePath: string) => void): HtmlParsedAnswer {
    const citations: string[] = [];

    // trim any whitespace from the end of the answer after removing follow-up questions
    let parsedAnswer = answer.trim();

    // Omit incomplete content during streaming
    if (isStreaming) {
        // Handle incomplete citations
        let lastIndex = parsedAnswer.length;
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === "]") {
                break;
            } else if (parsedAnswer[i] === "[") {
                lastIndex = i;
                break;
            }
        }

        // Handle incomplete HTML tags
        const lastOpeningTagIndex = parsedAnswer.lastIndexOf("<");
        const lastClosingTagIndex = parsedAnswer.lastIndexOf(">");
        if (lastOpeningTagIndex > lastClosingTagIndex) {
            lastIndex = Math.min(lastIndex, lastOpeningTagIndex);
        }

        parsedAnswer = parsedAnswer.substring(0, lastIndex);
    }

    const parts = parsedAnswer.split(/\[([^\]]+)]/g);

    const fragments: ReactElement[] = parts.map((part, index) => {
        if (index % 2 === 0) {
            // Check if the part contains HTML (specifically tables)
            const hasHtml = /<\/?[a-z][\s\S]*>/i.test(part);

            if (hasHtml) {
                // Sanitize HTML content and render as HTML
                const sanitizedHtml = DOMPurify.sanitize(part, {
                    ALLOWED_TAGS: [
                        "table",
                        "tr",
                        "td",
                        "th",
                        "thead",
                        "tbody",
                        "tfoot",
                        "caption",
                        "colgroup",
                        "col",
                        "p",
                        "br",
                        "strong",
                        "em",
                        "u",
                        "span",
                        "div",
                        "h1",
                        "h2",
                        "h3",
                        "h4",
                        "h5",
                        "h6"
                    ],
                    ALLOWED_ATTR: ["class", "id", "colspan", "rowspan", "style"]
                });

                return <div key={index} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} className="html-content" />;
            } else {
                // Plain text content
                return <Fragment key={index}>{part}</Fragment>;
            }
        } else {
            let citationIndex: number;
            if (citations.indexOf(part) !== -1) {
                citationIndex = citations.indexOf(part) + 1;
            } else {
                citations.push(part);
                citationIndex = citations.length;
            }

            return (
                <sup
                    key={index}
                    className={"cursor-pointer bg-blue-200 px-0.5 hover:underline"}
                    title={DOMPurify.sanitize(part)}
                    onClick={() => onCitationClicked(part)}
                >
                    {citationIndex}
                </sup>
            );
        }
    });

    return {
        answerFragments: fragments,
        citations
    };
}
